/**
 * FERP Portal - Gantt Chart Page
 *
 * Timeline visualization for tasks with dependency arrows.
 * Complements the Workflow State View (kanban board) with a
 * time-based perspective of the same data.
 *
 * FERP Principles:
 * - Dumb Frontend: All data from backend Task/Dependency primitives
 * - NO hardcoded values: Status/priority from generated enums
 * - Repository pattern: All data through generated Apollo hooks
 */

import { useState } from 'react';
import {
  Container, Group, Stack, Text, Title, Button, Alert, Switch,
  Loader, ThemeIcon,
} from '@mantine/core';
import {
  IconTimeline, IconRefresh, IconAlertTriangle, IconEye,
  IconLayoutColumns, IconCalendar,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { GanttChart, type GanttZoom } from '@/components/gantt/GanttChart';
import { useGanttData } from '@/hooks/useGanttData';
import { GANTT_DEMO_TASKS, GANTT_DEMO_DEPENDENCIES } from './ganttDemoData';
import type { DependencyFieldsFragment } from '@/generated/graphql';

export function GanttPage() {
  const navigate = useNavigate();
  const [demoMode, setDemoMode] = useState(true);
  const [zoom, setZoom] = useState<GanttZoom>('week');

  const { tasks, dependencies, loading, error, refetch } = useGanttData(
    demoMode
      ? { demoTasks: GANTT_DEMO_TASKS, demoDependencies: GANTT_DEMO_DEPENDENCIES as DependencyFieldsFragment[] }
      : undefined,
  );

  if (error && !demoMode) {
    return (
      <Container fluid py="md" px="md">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Gantt Data">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid py="md" px="md">
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            <ThemeIcon variant="light" size="lg" color="teal">
              <IconTimeline size={20} />
            </ThemeIcon>
            <div>
              <Title order={3}>Gantt View</Title>
              <Text size="xs" c="dimmed">
                Timeline visualization of tasks and dependencies
                {tasks.length > 0 && ` \u2022 ${tasks.length} tasks`}
                {dependencies.length > 0 && ` \u2022 ${dependencies.length} dependencies`}
                {demoMode && ' \u2022 Demo Mode'}
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button
              leftSection={<IconLayoutColumns size={14} />}
              variant="subtle"
              size="xs"
              onClick={() => navigate('/ferp-core/workflow-state')}
            >
              Board View
            </Button>
            <Button
              leftSection={<IconCalendar size={14} />}
              variant="subtle"
              size="xs"
              onClick={() => navigate('/ferp-core/calendar')}
            >
              Calendar
            </Button>
            <Switch
              label="Demo"
              size="xs"
              checked={demoMode}
              onChange={(e) => setDemoMode(e.currentTarget.checked)}
              thumbIcon={demoMode ? <IconEye size={10} /> : undefined}
            />
            <Button
              leftSection={<IconRefresh size={14} />}
              variant="light"
              onClick={() => refetch()}
              loading={loading}
              size="xs"
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Chart */}
        {loading && tasks.length === 0 ? (
          <Group justify="center" py="xl"><Loader /></Group>
        ) : (
          <GanttChart
            tasks={tasks}
            dependencies={dependencies}
            zoom={zoom}
            onZoomChange={setZoom}
          />
        )}
      </Stack>
    </Container>
  );
}

export default GanttPage;
